/** New email from the shared applications@ mailbox (Dean, 12 Oct 2026) — To / Cc / Subject / rich body / attachments /
 *  team templates / contact lookup, optional "also log on an application" (posts the sent email as a Monday update, like
 *  Match & Reply). No portal sign-off: the mailbox's own signature applies. */
import { useEffect, useRef, useState } from "react";
import { Loader2, Mail, Paperclip, Search, Send, X } from "lucide-react";
import { toast } from "sonner";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { RichTextEditor, textToEditorHtml } from "@/components/RichTextEditor";
import { EmailTemplatesMenu, firstNameOf, insertTemplateHtml } from "@/components/EmailTemplatesMenu";
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { EMAIL_RE, RecipientInput } from "@/pages/inbox/RecipientInput";
import { appInboxSearchApps, uploadInboxAttachment } from "@/lib/api";

const fmtSize = (n) => (n > 1024 * 1024 ? `${(n / 1024 / 1024).toFixed(1)} MB` : `${Math.max(1, Math.round(n / 1024))} KB`);
const MAX_TOTAL = 25 * 1024 * 1024;
const toTokens = (s) => (s || "").split(/[;,\s]+/).map((a) => a.trim().toLowerCase()).filter(Boolean).map((email) => ({ email, name: "", raw: true, valid: EMAIL_RE.test(email) }));

/** Pick the Monday application the email should be logged on — SF number or company search, LIVE first. */
export function AppPicker({ value, onChange, testId = "compose-app" }) {
  const [search, setSearch] = useState("");
  const [rows, setRows] = useState([]);
  const [busy, setBusy] = useState(false);
  useEffect(() => { if (value && !rows.some((r) => r.item_id === value.item_id)) setRows((r) => [value, ...r]); }, [value]); // eslint-disable-line react-hooks/exhaustive-deps
  const run = async () => {
    if (search.trim().length < 2) return;
    setBusy(true);
    try {
      const r = await appInboxSearchApps(search.trim());
      const rec = (r.matches || []).filter((m) => m.item_id);
      setRows(rec);
      if (rec.length === 1) onChange(rec[0]);
      if (!rec.length) toast.info(`No application found for “${search.trim()}”`);
    } catch (e) { toast.error(e.response?.data?.detail || e.message); } finally { setBusy(false); }
  };
  return (
    <div className="space-y-1.5" data-testid={`${testId}-picker`}>
      <div className="flex gap-1">
        <Input value={search} onChange={(e) => setSearch(e.target.value)} onKeyDown={(e) => e.key === "Enter" && run()}
          placeholder="Search SF-27512 or company name" className="h-7 text-xs bg-white" data-testid={`${testId}-search`} />
        <Button size="sm" variant="outline" className="h-7 px-2" disabled={busy || search.trim().length < 2} onClick={run} data-testid={`${testId}-search-btn`}>
          {busy ? <Loader2 className="w-3 h-3 animate-spin" /> : <Search className="w-3 h-3" />}
        </Button>
      </div>
      {rows.length > 0 && (
        <div className="border rounded divide-y max-h-36 overflow-y-auto bg-white">
          {rows.slice(0, 8).map((m) => (
            <label key={m.item_id} className="flex items-center gap-2 px-2 py-1.5 text-xs cursor-pointer hover:bg-slate-50">
              <input type="radio" name={`${testId}-target`} checked={value?.item_id === m.item_id} onChange={() => onChange(m)} data-testid={`${testId}-target-${m.sf}`} />
              <span className="font-mono font-semibold">{m.sf}</span>
              <span className="truncate flex-1">{m.name || m.customer_name}</span>
              {m.is_live && <Badge variant="outline" className="text-[9px] bg-green-50 text-green-700 border-green-300">LIVE</Badge>}
              <Badge variant="outline" className="text-[9px]">{m.status || "—"}</Badge>
            </label>
          ))}
        </div>
      )}
      {!rows.length && <p className="text-[10px] text-muted-foreground">Type an SF number or company and press Enter — or pick a recipient that belongs to an application above.</p>}
    </div>
  );
}

export function AppComposeDialog({ api, me, fromLabel, prefill = {}, onClose, onSent }) {
  const [to, setTo] = useState(() => toTokens(prefill.to));
  const [cc, setCc] = useState([]);
  const [showCc, setShowCc] = useState(false);
  const [subject, setSubject] = useState(prefill.subject || "");
  const [body, setBody] = useState(prefill.body || "");
  const [bodyHtml, setBodyHtml] = useState(() => textToEditorHtml(prefill.body || ""));
  const [logOn, setLogOn] = useState(!!prefill.app);
  const [picked, setPicked] = useState(prefill.app || null);
  const [files, setFiles] = useState([]);
  const [progress, setProgress] = useState(null);
  const [sending, setSending] = useState(false);
  const fileRef = useRef(null);
  const totalBytes = files.reduce((s, f) => s + f.size, 0);
  const badAddr = [...to, ...cc].find((a) => a.valid === false || !EMAIL_RE.test(a.email));
  const canSend = to.length > 0 && !badAddr && subject.trim() && body.trim() && (!logOn || picked);

  // a recipient that belongs to a Monday application suggests logging the email on that deal
  const pickCase = (c) => { if (!picked && c?.item_id) { setPicked({ item_id: c.item_id, sf: c.sf, name: c.customer_name, status: c.status, is_live: c.is_live }); setLogOn(true); } };

  const addFiles = (list) => {
    const next = [...files];
    Array.from(list || []).forEach((f) => { if (!next.some((x) => x.name === f.name && x.size === f.size)) next.push(f); });
    if (next.reduce((s, f) => s + f.size, 0) > MAX_TOTAL) { toast.error("Attachments must total under 25 MB"); return; }
    setFiles(next);
  };

  const submit = async () => {
    setSending(true);
    try {
      const attachments = [];
      for (let i = 0; i < files.length; i++) {
        setProgress({ name: files[i].name, i: i + 1, n: files.length, pct: 0 });
        attachments.push(await uploadInboxAttachment(api.uploadChunk, files[i], (pct) => setProgress((p) => ({ ...p, pct }))));
      }
      setProgress(null);
      const toList = to.map((t) => t.email), ccList = cc.map((t) => t.email);
      const r = await api.compose({ to: toList, cc: ccList, subject: subject.trim(), body, body_html: bodyHtml,
        item_id: logOn && picked ? picked.item_id : "", sf: logOn && picked ? picked.sf : "", actor: me?.name || me?.email || "", attachments });
      (r.warnings || []).forEach((w) => toast.warning(w));
      toast.success(`Email sent to ${toList.join(", ")}${r.attachments ? ` with ${r.attachments} attachment${r.attachments === 1 ? "" : "s"}` : ""}${r.logged_to ? ` · logged on ${r.logged_to}` : ""}`);
      onSent?.(r);
    } catch (e) { toast.error(e.response?.data?.detail || e.message); } finally { setSending(false); setProgress(null); }
  };

  return (
    <Dialog open onOpenChange={(v) => !v && !sending && onClose()}>
      <DialogContent className="max-w-4xl max-h-[94vh] overflow-y-auto" aria-describedby={undefined} data-testid="compose-dialog"
        onDragOver={(e) => e.preventDefault()} onDrop={(e) => { e.preventDefault(); addFiles(e.dataTransfer.files); }}>
        <DialogHeader>
          <DialogTitle className="text-base flex items-center gap-2">
            <Mail className="w-4 h-4 text-blue-600" />New email
            <span className="text-[11px] font-normal text-muted-foreground">from&nbsp;{fromLabel || "applications@"}</span>
          </DialogTitle>
        </DialogHeader>
        <div className="space-y-2.5">
          <div className="flex items-start gap-2">
            <span className="text-xs w-12 text-muted-foreground pt-2">To</span>
            <RecipientInput api={api} value={to} onChange={setTo} onPickCase={pickCase} autoFocus testid="compose-to"
              placeholder="Type a broker, company, SF number or an email address" />
            {!showCc && <Button type="button" variant="ghost" size="sm" className="h-8 text-[11px]" onClick={() => setShowCc(true)} data-testid="compose-show-cc">Cc</Button>}
          </div>
          {showCc && (
            <div className="flex items-start gap-2">
              <span className="text-xs w-12 text-muted-foreground pt-2">Cc</span>
              <RecipientInput api={api} value={cc} onChange={setCc} testid="compose-cc" placeholder="optional" />
            </div>
          )}
          <div className="flex items-center gap-2">
            <span className="text-xs w-12 text-muted-foreground">Subject</span>
            <Input value={subject} onChange={(e) => setSubject(e.target.value)} placeholder="Subject" className="h-8 text-sm" data-testid="compose-subject" />
          </div>
          {badAddr && <p className="text-[11px] text-rose-700" data-testid="compose-bad-address">"{badAddr.email}" doesn't look like an email address</p>}
          <div className="flex items-center justify-between">
            <span className="text-[11px] font-medium text-slate-600">Message</span>
            <EmailTemplatesMenu dept="applications" region={api.region} testId="compose-templates"
              context={{ first_name: firstNameOf(to[0]?.name), customer_name: picked?.name || to[0]?.name || "", sf_number: picked?.sf || "", my_name: me?.name || "" }}
              getCurrent={() => ({ body_html: bodyHtml, body_text: body, subject })}
              onInsert={({ body_html, subject: tSubject }) => { setBodyHtml((cur) => insertTemplateHtml(cur, body_html)); if (tSubject && !subject.trim()) setSubject(tSubject); }} />
          </div>
          <RichTextEditor value={bodyHtml} onChange={({ html, text }) => { setBodyHtml(html); setBody(text); }} minHeight={240} testId="compose-body"
            placeholder={`Type your email — it goes out from ${fromLabel || "applications@"} with the mailbox signature`} />
          <div className="border rounded p-2 bg-slate-50/60" data-testid="compose-attachments">
            <div className="flex items-center gap-2 flex-wrap">
              <Button type="button" size="sm" variant="outline" className="h-7 text-[11px]" onClick={() => fileRef.current?.click()} disabled={sending} data-testid="compose-attach-btn">
                <Paperclip className="w-3 h-3 mr-1" />Attach files
              </Button>
              <input ref={fileRef} type="file" multiple className="hidden" onChange={(e) => { addFiles(e.target.files); e.target.value = ""; }} data-testid="compose-file-input" />
              <span className="text-[10px] text-muted-foreground">{files.length ? `${files.length} file${files.length === 1 ? "" : "s"} · ${fmtSize(totalBytes)}` : "or drop files on this dialog — up to 25 MB in total"}</span>
            </div>
            {files.length > 0 && (
              <ul className="mt-2 flex flex-wrap gap-1.5">
                {files.map((f, i) => (
                  <li key={`${f.name}-${i}`} className="inline-flex items-center gap-1 border rounded bg-white px-2 py-0.5 text-[11px] max-w-[260px]" data-testid={`compose-attachment-${i}`}>
                    <Paperclip className="w-3 h-3 text-slate-400 shrink-0" /><span className="truncate">{f.name}</span><span className="text-slate-400 shrink-0">{fmtSize(f.size)}</span>
                    <button type="button" className="text-slate-400 hover:text-red-600 shrink-0" onClick={() => setFiles(files.filter((_, j) => j !== i))} disabled={sending} data-testid={`compose-attachment-remove-${i}`}><X className="w-3 h-3" /></button>
                  </li>
                ))}
              </ul>
            )}
            {progress && <div className="mt-2 text-[10px] text-blue-700" data-testid="compose-upload-progress">Uploading {progress.name} ({progress.i}/{progress.n}) — {progress.pct}%</div>}
          </div>
          <label className="flex items-center gap-2 text-xs cursor-pointer">
            <input type="checkbox" checked={logOn} onChange={(e) => setLogOn(e.target.checked)} data-testid="compose-log-toggle" />
            Also log this email as an update on an application (Monday)
          </label>
          {logOn && <div className="border rounded p-2 bg-blue-50/40" data-testid="compose-app-section"><AppPicker value={picked} onChange={setPicked} /></div>}
        </div>
        <DialogFooter>
          <Button variant="ghost" onClick={onClose} disabled={sending}>Cancel</Button>
          <Button onClick={submit} disabled={sending || !canSend} data-testid="compose-submit">
            {sending ? <Loader2 className="w-4 h-4 mr-1 animate-spin" /> : <Send className="w-4 h-4 mr-1" />}
            {sending ? (progress ? "Uploading…" : "Sending…") : logOn && picked ? `Send & log on ${picked.sf}` : "Send email"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
